import {
  useAppSelector,
  getIsParkingFeatureVisibleForBuilding,
  getIsLoadingParkingLots,
  getBuildingsForSelection,
  getFloorsByBuildingId,
  getAreasByBuildingId,
  getFilteredAndSortedParkingLots,
  setIsNewUserSession,
  updateParkingLocationPreferences,
  updateUser,
  updateWorkspaceLocationPreferences,
  withAsyncThunkErrorHandling,
  useAppDispatch,
  getUser,
  getIsNew,
} from '@lib/store';
import {normalizePayload} from '@lib/utils/src/util/preferences';
import {WorkspaceLocationDto} from '@lib/infrastructure';
import {useState} from 'react';
import {getParkingReservationLabel, getWorkspaceReservationLabel} from '../../util';

interface Selection {
  buildingId: string | null;
  floorId: string | null;
  areaId: string | null;
  parkingLotId?: string | null;
  parkingZoneId?: string | null;
  deskAmenities: string[];
  isDeskRequired: boolean;
}

export const usePreferencesOverview = ({
  buildingId: providedBuildingId,
  floorId,
  areaId,
  parkingLotId = null,
  parkingZoneId = null,
  deskAmenities,
  isDeskRequired = false,
}: Selection) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const isNew = useAppSelector(getIsNew);

  // Cached selector key cannot be `null` or `undefined`
  const buildingId = providedBuildingId ?? '';

  const [isSaving, setIsSaving] = useState(false);

  const isParkingFeatureVisible = useAppSelector((state) => getIsParkingFeatureVisibleForBuilding(state, buildingId));
  const isLoadingParkingLots = useAppSelector(getIsLoadingParkingLots);

  const buildings = useAppSelector(getBuildingsForSelection);
  const floors = useAppSelector((state) => getFloorsByBuildingId(state, buildingId));
  const areas = useAppSelector((state) => getAreasByBuildingId(state, buildingId));
  const parkingLots = useAppSelector((state) => getFilteredAndSortedParkingLots(state, buildingId));
  const parkingZones = parkingLots.flatMap((lot) => lot.parkingZones);

  const selectedBuilding = buildings.find((building) => building.id === buildingId);
  const selectedFloor = floors.find((floor) => floor.id === floorId);
  const selectedArea = areas.find((area) => area.id === areaId);
  const selectedParkingLot = parkingLots.find((lot) => lot.id === parkingLotId);
  const selectedParkingZone = parkingZones.find((zone) => zone.id === parkingZoneId);

  const workspaceLabel = getWorkspaceReservationLabel({
    buildingName: selectedBuilding?.name,
    floorName: selectedFloor?.name,
    areaName: selectedArea?.name,
  });
  const parkingLabel = getParkingReservationLabel({
    parkingLotName: selectedParkingLot?.name,
    parkingZoneName: selectedParkingZone?.name,
  });

  const saveWorkplacePreferences = async () => {
    const payload = normalizePayload<WorkspaceLocationDto>({
      floorId,
      areaId,
      deskAmenities,
      isDeskRequired,
    });

    return await dispatch(withAsyncThunkErrorHandling(() => updateWorkspaceLocationPreferences(payload)));
  };

  const saveParkingPreferences = async () => {
    return await dispatch(
      withAsyncThunkErrorHandling(() =>
        updateParkingLocationPreferences({
          parkingLotId,
          parkingZoneId,
        }),
      ),
    );
  };

  const saveUser = async () => {
    const response = await dispatch(withAsyncThunkErrorHandling(() => updateUser({...user, isNew: false})));

    if (response.success) {
      dispatch(setIsNewUserSession(false));
    }
  };

  const savePreferences = async () => {
    setIsSaving(true);

    const workplacePromise = saveWorkplacePreferences();
    const parkingPromise = isParkingFeatureVisible ? saveParkingPreferences() : Promise.resolve({success: true});

    const [workplaceResponse, parkingResponse] = await Promise.all([workplacePromise, parkingPromise]);

    if (workplaceResponse.success && parkingResponse.success && isNew) {
      await saveUser();
    }

    setIsSaving(false);
  };

  return {
    isParkingFeatureVisible,
    isLoadingParkingLots,
    isSaving,

    selectedBuilding,
    selectedFloor,
    selectedArea,
    selectedParkingLot,
    selectedParkingZone,

    savePreferences,

    workspaceLabel,
    parkingLabel,
  };
};
